/**
 * SMS Template Modal
 *
 * Create/Edit modal for SMS delivery templates with:
 * - Grouped variable picker with cursor-position insertion
 * - Live preview with sample value resolution
 * - SMS character/segment counter
 * - Document and Form template type support
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  cn,
  Button,
  Input,
  Label,
  Switch,
  Textarea,
} from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@respectly/ui';
import {
  MessageSquare,
  FileText,
  Loader2,
  Eye,
  Hash,
  Star,
  User,
  Building2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type {
  SmsTemplate,
  SmsTemplateType,
  SmsTemplateVariable,
  CreateSmsTemplateDto,
  UpdateSmsTemplateDto,
} from '@respectly/shared-types';
import {
  DOCUMENT_SMS_VARIABLES,
  FORM_SMS_VARIABLES,
  CONTACT_SMS_VARIABLES,
  ORGANISATION_SMS_VARIABLES,
} from '@respectly/shared-types';

// ============================================
// Default Template Bodies
// ============================================

const DEFAULT_TEMPLATE_BODIES: Record<SmsTemplateType, string> = {
  document:
    'Hi {{firstName}}, {{orgName}} has sent you a document to sign: {{documentName}}. Please review and sign here: {{signingUrl}}',
  form: 'Hi {{firstName}}, {{orgName}} has shared a form with you: {{formName}}. Please complete it here: {{formUrl}}',
};

// ============================================
// Sample Preview Data
// ============================================

const PREVIEW_DATA: Record<string, string> = {
  firstName: 'Jane',
  lastName: 'Smith',
  fullName: 'Jane Smith',
  documentName: 'Service Agreement',
  signingUrl: 'https://sign.nexus.app/abc123',
  expiresAt: '03/15/2026',
  formName: 'New Client Intake',
  formUrl: 'https://forms.nexus.app/intake',
  orgName: 'Nexus Solutions',
  orgCity: 'Austin',
  orgState: 'TX',
  orgCountry: 'US',
  tenantName: 'Nexus Solutions',
};

// ============================================
// Helper Functions
// ============================================

function resolvePreview(body: string): string {
  return body.replace(/\{\{(\w+)\}\}/g, (_, key) => PREVIEW_DATA[key] ?? `{{${key}}}`);
}

function getSmsSegmentInfo(text: string): { chars: number; segments: number; encoding: string } {
  const chars = text.length;
  // Check for non-GSM characters
  const gsmPattern = /^[ -~\n\r\t]*$/;
  const isGsm = gsmPattern.test(text);
  const singleLimit = isGsm ? 160 : 70;
  const multiLimit = isGsm ? 153 : 67;
  const segments = chars === 0 ? 0 : chars <= singleLimit ? 1 : Math.ceil(chars / multiLimit);
  return { chars, segments, encoding: isGsm ? 'GSM-7' : 'Unicode' };
}

// ============================================
// Variable Group Component
// ============================================

interface VariableGroupProps {
  label: string;
  icon: React.ElementType;
  variables: SmsTemplateVariable[];
  accentClass: string;
  onInsert: (placeholder: string) => void;
  collapsed?: boolean;
  onToggle?: () => void;
}

function VariableGroup({
  label,
  icon: Icon,
  variables,
  accentClass,
  onInsert,
  collapsed = false,
  onToggle,
}: VariableGroupProps) {
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 w-full group"
      >
        <Icon className={cn('w-3 h-3 shrink-0', accentClass)} />
        <span className={cn('text-[11px] font-semibold uppercase tracking-wider', accentClass)}>
          {label}
        </span>
        {onToggle && (
          collapsed
            ? <ChevronDown className="w-3 h-3 ml-auto text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
            : <ChevronUp className="w-3 h-3 ml-auto text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
        )}
      </button>
      {!collapsed && (
        <div className="flex flex-wrap gap-1.5 pl-4">
          {variables.map((v) => (
            <button
              key={v.key}
              type="button"
              title={v.description}
              onClick={() => onInsert(v.placeholder)}
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded text-[11px] font-mono',
                'border transition-all duration-150',
                'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700',
                'text-slate-600 dark:text-slate-300',
                'hover:scale-105 hover:shadow-sm active:scale-95',
                'hover:border-current',
                accentClass.includes('blue') && 'hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 dark:hover:bg-blue-900/20 dark:hover:border-blue-700 dark:hover:text-blue-300',
                accentClass.includes('violet') && 'hover:bg-violet-50 hover:border-violet-300 hover:text-violet-700 dark:hover:bg-violet-900/20 dark:hover:border-violet-700 dark:hover:text-violet-300',
                accentClass.includes('emerald') && 'hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:border-emerald-700 dark:hover:text-emerald-300',
                accentClass.includes('amber') && 'hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700 dark:hover:bg-amber-900/20 dark:hover:border-amber-700 dark:hover:text-amber-300',
              )}
            >
              {v.placeholder}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Form State
// ============================================

interface FormState {
  name: string;
  type: SmsTemplateType;
  body: string;
  isDefault: boolean;
  isActive: boolean;
}

// ============================================
// Props
// ============================================

export interface SmsTemplateModalProps {
  template?: SmsTemplate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultType?: SmsTemplateType;
  onSubmit: (data: CreateSmsTemplateDto | UpdateSmsTemplateDto) => Promise<boolean>;
  isLoading?: boolean;
}

// ============================================
// Component
// ============================================

export function SmsTemplateModal({
  template,
  open,
  onOpenChange,
  defaultType = 'document',
  onSubmit,
  isLoading = false,
}: SmsTemplateModalProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [form, setForm] = useState<FormState>({
    name: '',
    type: defaultType,
    body: '',
    isDefault: false,
    isActive: true,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const isEditing = !!template;
  const variableSet = form.type === 'document' ? DOCUMENT_SMS_VARIABLES : FORM_SMS_VARIABLES;

  // Group variables
  const contactVars = CONTACT_SMS_VARIABLES;
  const typeSpecificVars = variableSet.filter(
    (v) => v.group === 'document' || v.group === 'form'
  );
  const orgVars = ORGANISATION_SMS_VARIABLES;

  // SMS stats
  const segmentInfo = getSmsSegmentInfo(form.body);
  const isNearLimit = segmentInfo.chars > 130 && segmentInfo.chars <= 160;
  const isOverSingle = segmentInfo.chars > 160;

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open && template) {
      setForm({
        name: template.name,
        type: template.type,
        body: template.body,
        isDefault: template.isDefault,
        isActive: template.isActive,
      });
    } else if (open) {
      setForm({
        name: '',
        type: defaultType,
        body: DEFAULT_TEMPLATE_BODIES[defaultType],
        isDefault: false,
        isActive: true,
      });
    }
    setErrors({});
    setShowPreview(false);
  }, [open, template, defaultType]);

  const handleChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  // Insert variable at cursor position
  const handleInsertVariable = useCallback(
    (placeholder: string) => {
      const ta = textareaRef.current;
      if (!ta) {
        setForm((prev) => ({ ...prev, body: prev.body + placeholder }));
        return;
      }
      const start = ta.selectionStart ?? form.body.length;
      const end = ta.selectionEnd ?? form.body.length;
      const newBody = form.body.slice(0, start) + placeholder + form.body.slice(end);
      setForm((prev) => ({ ...prev, body: newBody }));
      requestAnimationFrame(() => {
        const pos = start + placeholder.length;
        ta.setSelectionRange(pos, pos);
        ta.focus();
      });
    },
    [form.body]
  );

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Template name is required';
    if (!form.body.trim()) newErrors.body = 'Message body is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const data = isEditing
      ? ({
          name: form.name.trim(),
          body: form.body.trim(),
          isDefault: form.isDefault,
          isActive: form.isActive,
        } satisfies UpdateSmsTemplateDto)
      : ({
          type: form.type,
          name: form.name.trim(),
          body: form.body.trim(),
          isDefault: form.isDefault,
          isActive: form.isActive,
        } satisfies CreateSmsTemplateDto);
    const success = await onSubmit(data);
    if (success) onOpenChange(false);
  };

  const typeLabel = form.type === 'document' ? 'Document' : 'Form';
  const TypeIcon = form.type === 'document' ? FileText : MessageSquare;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-100 to-teal-200 dark:from-teal-900/40 dark:to-teal-800/40 flex items-center justify-center shrink-0">
              <TypeIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <DialogTitle>
                {isEditing ? 'Edit SMS Template' : `New ${typeLabel} SMS Template`}
              </DialogTitle>
              <DialogDescription>
                {isEditing
                  ? 'Update the template body and settings'
                  : `Create an SMS template for ${typeLabel.toLowerCase()} delivery notifications`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          {/* Name + Type Row */}
          <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
            <div className="space-y-1.5">
              <Label htmlFor="sms-template-name">Template Name</Label>
              <Input
                id="sms-template-name"
                placeholder="e.g. Standard Document Signing Request"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={errors.name}
              />
            </div>

            {/* Type badge (read-only when editing, shown as indicator) */}
            <div className="space-y-1.5">
              <Label>Type</Label>
              <div
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 h-11 rounded-lg border text-sm font-medium',
                  form.type === 'document'
                    ? 'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-900/20 dark:border-violet-800 dark:text-violet-300'
                    : 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300'
                )}
              >
                <TypeIcon className="w-3.5 h-3.5" />
                {typeLabel}
              </div>
            </div>
          </div>

          {/* Variable Picker */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Insert Variable
              </Label>
              <span className="text-[11px] text-slate-400 dark:text-slate-500">
                Click a variable to insert at cursor
              </span>
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-3 space-y-3">
              <VariableGroup
                label="Contact"
                icon={User}
                variables={contactVars}
                accentClass="text-blue-600 dark:text-blue-400"
                onInsert={handleInsertVariable}
                collapsed={collapsedGroups['contact']}
                onToggle={() => toggleGroup('contact')}
              />
              <div className="border-t border-slate-200 dark:border-slate-700/60" />
              <VariableGroup
                label={typeLabel}
                icon={TypeIcon}
                variables={typeSpecificVars}
                accentClass="text-violet-600 dark:text-violet-400"
                onInsert={handleInsertVariable}
                collapsed={collapsedGroups['type']}
                onToggle={() => toggleGroup('type')}
              />
              <div className="border-t border-slate-200 dark:border-slate-700/60" />
              <VariableGroup
                label="Organization"
                icon={Building2}
                variables={orgVars}
                accentClass="text-emerald-600 dark:text-emerald-400"
                onInsert={handleInsertVariable}
                collapsed={collapsedGroups['org']}
                onToggle={() => toggleGroup('org')}
              />
            </div>
          </div>

          {/* Message Body */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-template-body">Message Body</Label>
              <button
                type="button"
                onClick={() => setShowPreview((p) => !p)}
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors',
                  showPreview
                    ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                )}
              >
                <Eye className="w-3 h-3" />
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>
            </div>

            <div className="relative">
              <Textarea
                ref={textareaRef}
                id="sms-template-body"
                value={form.body}
                onChange={(e) => handleChange('body', e.target.value)}
                error={errors.body}
                rows={4}
                className="font-mono text-sm leading-relaxed"
              />
            </div>

            {/* Character & Segment Counter */}
            <div className="flex items-center justify-between px-0.5">
              <div className="flex items-center gap-1.5">
                <Hash className="w-3 h-3 text-slate-400" />
                <span
                  className={cn(
                    'text-[11px] font-mono transition-colors',
                    isOverSingle
                      ? 'text-amber-600 dark:text-amber-400'
                      : isNearLimit
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-slate-400 dark:text-slate-500'
                  )}
                >
                  {segmentInfo.chars} chars
                  {isOverSingle && (
                    <span className="ml-1">
                      · {segmentInfo.segments} segment{segmentInfo.segments !== 1 ? 's' : ''}
                    </span>
                  )}
                </span>
              </div>
              {segmentInfo.chars > 0 && (
                <span className="text-[11px] text-slate-400 dark:text-slate-500">
                  {segmentInfo.encoding}
                </span>
              )}
            </div>

            {/* Live Preview */}
            {showPreview && form.body && (
              <div className="mt-2 rounded-lg border border-teal-200/60 dark:border-teal-800/40 bg-teal-50/40 dark:bg-teal-900/10 overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-teal-200/60 dark:border-teal-800/40 bg-teal-50 dark:bg-teal-900/20">
                  <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                  <span className="text-[11px] font-medium text-teal-700 dark:text-teal-400 uppercase tracking-wider">
                    Live Preview (sample data)
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap font-sans">
                    {resolvePreview(form.body)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Toggles Row */}
          <div className="flex items-center gap-6 pt-1 border-t border-slate-100 dark:border-slate-800">
            {/* Set as Default */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Star className="w-3.5 h-3.5 text-amber-500" />
                <Label htmlFor="sms-template-default" className="cursor-pointer text-sm">
                  Set as Default
                </Label>
              </div>
              <Switch
                id="sms-template-default"
                checked={form.isDefault}
                onCheckedChange={(v) => handleChange('isDefault', v)}
              />
            </div>

            <div className="h-4 w-px bg-slate-200 dark:bg-slate-700" />

            {/* Active */}
            <div className="flex items-center gap-3">
              <Label htmlFor="sms-template-active" className="cursor-pointer text-sm">
                Active
              </Label>
              <Switch
                id="sms-template-active"
                checked={form.isActive}
                onCheckedChange={(v) => handleChange('isActive', v)}
              />
            </div>

            {form.isDefault && (
              <p className="ml-auto text-[11px] text-amber-600 dark:text-amber-400">
                Will replace the current default for {typeLabel.toLowerCase()} templates
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isEditing ? 'Saving…' : 'Creating…'}
                </>
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Create Template'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
